import React, { useEffect, useState } from 'react';
import { Send, Mail, Smartphone, Bell, Users, ChevronDown, History, AlertCircle, CheckCircle2 } from 'lucide-react';
import AdminLayout from './AdminLayout';
import { listFamilies, previewAudience, sendBroadcast, listBroadcasts } from '../../lib/admin';

const COUNTRIES = ['GT', 'MX', 'US', 'ES', 'CR', 'HN', 'CO', 'AR', 'PA', 'SV', 'NI'];

export default function AdminBroadcasts() {
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [channels, setChannels] = useState({ in_app: true, email: false, push: false });
    const [audType, setAudType] = useState('all');
    const [audValue, setAudValue] = useState('');
    const [families, setFamilies] = useState([]);
    const [previewCount, setPreviewCount] = useState(null);
    const [sending, setSending] = useState(false);
    const [history, setHistory] = useState([]);
    const [status, setStatus] = useState(null);

    useEffect(() => {
        listFamilies().then(setFamilies).catch(() => {});
        refreshHistory();
    }, []);

    useEffect(() => {
        const value = (audType === 'family' || audType === 'country') ? audValue : null;
        if (audType === 'family' && !audValue) { setPreviewCount(null); return; }
        if (audType === 'country' && !audValue) { setPreviewCount(null); return; }
        previewAudience(audType, value).then(setPreviewCount).catch(() => setPreviewCount(null));
    }, [audType, audValue]);

    const refreshHistory = () => listBroadcasts().then(setHistory).catch(() => {});

    const toggleChannel = (k) => setChannels({ ...channels, [k]: !channels[k] });

    const submit = async () => {
        setStatus(null);
        if (!title.trim() || !message.trim()) {
            setStatus({ ok: false, msg: 'Título y mensaje son requeridos' });
            return;
        }
        const selected = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
        if (selected.length === 0) {
            setStatus({ ok: false, msg: 'Selecciona al menos un canal' });
            return;
        }
        setSending(true);
        try {
            const result = await sendBroadcast({
                title: title.trim(),
                message: message.trim(),
                channels: selected,
                audience: { type: audType, value: audValue || null },
            });
            const inAppSent = result.results?.in_app?.sent || 0;
            const stubChannels = Object.entries(result.results || {})
                .filter(([, v]) => v?.stub)
                .map(([k]) => k);
            let msg = `Enviado a ${result.recipient_count} destinatarios (in-app: ${inAppSent})`;
            if (stubChannels.length) msg += ` · ${stubChannels.join(', ')} pendiente de integrar proveedor`;
            setStatus({ ok: true, msg });
            setTitle(''); setMessage('');
            refreshHistory();
        } catch (e) {
            setStatus({ ok: false, msg: e?.response?.data?.detail || 'Error al enviar' });
        } finally {
            setSending(false);
        }
    };

    return (
        <AdminLayout title="Mensajes a usuarios">
            <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <h3 className="text-sm font-bold text-brand-navy mb-4">Componer mensaje</h3>

                    <Label>Título</Label>
                    <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Ej: Nueva sección disponible" className="w-full h-11 px-3 bg-white border border-gray-200 rounded-xl text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 mb-4" data-testid="broadcast-title" />

                    <Label>Mensaje</Label>
                    <textarea value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000} rows={5} placeholder="Escribe el mensaje que verán los usuarios…" className="w-full p-3 bg-white border border-gray-200 rounded-xl text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 mb-4 resize-y" data-testid="broadcast-message" />

                    <Label>Canales de envío</Label>
                    <div className="grid grid-cols-3 gap-2 mb-4">
                        <ChannelToggle icon={Bell} label="In-App" active={channels.in_app} onClick={() => toggleChannel('in_app')} hint="Disponible ✅" testId="broadcast-channel-in_app" />
                        <ChannelToggle icon={Mail} label="Email" active={channels.email} onClick={() => toggleChannel('email')} hint="Por integrar" testId="broadcast-channel-email" />
                        <ChannelToggle icon={Smartphone} label="Push" active={channels.push} onClick={() => toggleChannel('push')} hint="Modo nativo" testId="broadcast-channel-push" />
                    </div>

                    <Label>Audiencia</Label>
                    <div className="mb-3">
                        <div className="relative">
                            <select value={audType} onChange={(e) => { setAudType(e.target.value); setAudValue(''); }} className="w-full h-11 pl-3 pr-10 bg-white border border-gray-200 rounded-xl text-sm appearance-none focus:border-brand focus:ring-2 focus:ring-brand/20" data-testid="broadcast-audience-type">
                                <option value="all">Todos los usuarios verificados</option>
                                <option value="premium">Solo usuarios Premium</option>
                                <option value="free">Solo usuarios del plan gratuito</option>
                                <option value="family">Familia específica</option>
                                <option value="country">Por país</option>
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>

                    {audType === 'family' && (
                        <div className="mb-3">
                            <select value={audValue} onChange={(e) => setAudValue(e.target.value)} className="w-full h-11 px-3 bg-white border border-gray-200 rounded-xl text-sm appearance-none" data-testid="broadcast-audience-family">
                                <option value="">Selecciona una familia…</option>
                                {families.map((f) => (
                                    <option key={f.id} value={f.id}>{f.name} ({f.code})</option>
                                ))}
                            </select>
                        </div>
                    )}
                    {audType === 'country' && (
                        <div className="mb-3">
                            <select value={audValue} onChange={(e) => setAudValue(e.target.value)} className="w-full h-11 px-3 bg-white border border-gray-200 rounded-xl text-sm appearance-none" data-testid="broadcast-audience-country">
                                <option value="">Selecciona un país…</option>
                                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    )}

                    {previewCount !== null && (
                        <div className="bg-brand/5 border border-brand/15 rounded-xl px-3 py-2 mb-4 flex items-center gap-2 text-sm" data-testid="broadcast-preview-count">
                            <Users className="w-4 h-4 text-brand" />
                            <span className="text-brand-navy font-semibold">{previewCount}</span>
                            <span className="text-gray-500">destinatarios coincidentes</span>
                        </div>
                    )}

                    {status && (
                        <div className={`mb-3 flex items-center gap-2 text-sm font-semibold ${status.ok ? 'text-emerald-600' : 'text-red-600'}`} data-testid="broadcast-status">
                            {status.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                            {status.msg}
                        </div>
                    )}

                    <button
                        onClick={submit}
                        disabled={sending}
                        className="w-full h-11 bg-brand text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                        data-testid="broadcast-send"
                    >
                        <Send className="w-4 h-4" />
                        {sending ? 'Enviando…' : 'Enviar mensaje'}
                    </button>
                </div>

                <div className="bg-white rounded-2xl border border-gray-200 p-5">
                    <div className="flex items-center gap-2 mb-4">
                        <History className="w-4 h-4 text-gray-500" />
                        <h3 className="text-sm font-bold text-brand-navy">Historial de envíos</h3>
                    </div>
                    <div className="space-y-3 max-h-[600px] overflow-y-auto" data-testid="broadcast-history">
                        {history.length === 0 && <p className="text-xs text-gray-400 italic">Aún no has enviado ningún mensaje.</p>}
                        {history.map((b) => (
                            <div key={b.id} className="border border-gray-100 rounded-xl p-3" data-testid={`broadcast-history-${b.id}`}>
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <p className="text-sm font-bold text-brand-navy truncate">{b.title}</p>
                                    <span className="text-[10px] text-gray-400 flex-shrink-0">{new Date(b.created_at).toLocaleString('es-ES')}</span>
                                </div>
                                <p className="text-xs text-gray-600 line-clamp-2 mb-2">{b.message}</p>
                                <div className="flex items-center gap-2 text-[10px]">
                                    <span className="text-gray-400">{b.recipient_count} destinatarios</span>
                                    {b.channels.map((c) => <span key={c} className="px-1.5 py-0.5 rounded bg-brand/10 text-brand font-bold uppercase">{c}</span>)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

function Label({ children }) {
    return <label className="text-[11px] uppercase tracking-wider font-bold text-gray-500 block mb-1.5">{children}</label>;
}
function ChannelToggle({ icon: Icon, label, active, onClick, hint, testId }) {
    return (
        <button onClick={onClick} data-testid={testId} className={`p-3 rounded-xl border-2 transition text-center ${active ? 'border-brand bg-brand/5' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
            <Icon className={`w-5 h-5 mx-auto mb-1 ${active ? 'text-brand' : 'text-gray-400'}`} />
            <div className={`text-xs font-bold ${active ? 'text-brand-navy' : 'text-gray-600'}`}>{label}</div>
            <div className="text-[9px] text-gray-400 mt-0.5">{hint}</div>
        </button>
    );
}
